/**
 * Swamithra — passwordless authentication for the user app (US-00-001).
 *
 * ONE ENTRY SCREEN, THREE CALLS. A number is typed and `lookupNumber` says
 * whether it is known; the profile fields are revealed only when it is not
 * (US-00-001.5/.6). `requestOtp` dispatches the code over WhatsApp — never SMS,
 * never email (US-00-001.7). `verifyOtp` is where the account is created, so a
 * request that is never verified leaves no user record behind.
 *
 * EVERY CALL HERE IS ANONYMOUS. `anonymous: true` suppresses the Authorization
 * header and, more importantly, any 401 handling: a wrong OTP is not a stale
 * session, and treating it as one would sign the person out of the session they
 * are in the middle of creating.
 *
 * THE ROUTES AND THE BODIES ARE THE SERVICE'S, NOT THIS PACKAGE'S. They are
 * `POST /auth/lookup`, `POST /auth/otp`, `POST /auth/otp/verify`, `POST
 * /auth/sign-out` and `GET /me`, exactly as the published specification lists
 * them, and every field name below is the one its decoder accepts — which
 * matters more here than anywhere else in the client, because that decoder
 * refuses a body carrying a field it does not know rather than ignoring it.
 */

import { z } from "zod"

import { mobileNumberSchema } from "../domain/phone"
import {
	numberLookupSchema,
	otpChallengeSchema,
	type OtpRequestInput,
	otpRequestSchema,
	type OtpVerifyInput,
	otpVerifySchema,
	sessionSchema,
	userSchema,
} from "../domain/user"
import { api, request } from "./client"
import { type ReadOptions } from "./options"

/**
 * Whether this number already has an account.
 *
 * Deliberately reveals registration status: the entry screen cannot decide
 * whether to show Full Name and User Type without it (US-00-001.6). The panel's
 * equivalent does the opposite — see ./panel-auth, where revealing it would be
 * an account-enumeration hole.
 */
export async function lookupNumber(mobileNumber: string, options: ReadOptions = {}) {
	return await api.post(
		"/auth/lookup",
		numberLookupSchema,
		// Normalised before it leaves, so `09876543210` and `+91 98765 43210` ask
		// about the same account (US-00-001.12). The whole passwordless flow keys
		// on this value; a raw one here means a duplicate account there.
		//
		// It goes over the wire as `mobile_number` — ./client applies the naming
		// convention, and the service's strict decoder answers anything else with
		// 400 `invalid_body`.
		{ mobileNumber: mobileNumberSchema.parse(mobileNumber) },
		{ ...options, anonymous: true },
	)
}

/**
 * Dispatches a code.
 *
 * THE PROFILE FIELDS TRAVEL FLAT. The entry screen holds them as a
 * `registration` object because that is how the form is shaped; the service
 * reads `full_name`, `email` and `user_type` beside `mobile_number` and rejects
 * anything else outright. Flattening is done here, at the boundary, rather than
 * by making the form's shape follow the wire.
 *
 * They are sent only for a number the lookup reported as unknown, and the server
 * validates them again — a client is not the authority on whether an account
 * exists (US-00-001.6).
 */
export async function requestOtp(body: OtpRequestInput, options: ReadOptions = {}) {
	const { mobileNumber, registration } = otpRequestSchema.parse(body)

	return await api.post(
		"/auth/otp",
		otpChallengeSchema,
		{
			mobileNumber,
			...(registration
				? {
						fullName: registration.fullName,
						userType: registration.userType,
						// Only when the user gave one: `email: null` is a field
						// the decoder accepts, but sending it for a registration
						// that had no address says something the form did not.
						...(registration.email ? { email: registration.email } : {}),
					}
				: {}),
		},
		{ ...options, anonymous: true },
	)
}

/**
 * Verifies a code and, for a new number, creates the account.
 *
 * A 401 here is a WRONG CODE, not an expired session, which is why this call is
 * anonymous and nothing about it disturbs a session that may already exist.
 */
export async function verifyOtp(body: OtpVerifyInput, options: ReadOptions = {}) {
	return await api.post("/auth/otp/verify", sessionSchema, otpVerifySchema.parse(body), {
		...options,
		anonymous: true,
	})
}

/**
 * Who the caller is — and the request that slides the idle window forward.
 *
 * `GET /me` IS THE SESSION PROBE, and it answers with the user record. The
 * service has no `/auth/session` route, and a client that asked for one got a
 * 404 it could not tell apart from "not signed in" — which sent every route
 * guard on web to the entry screen with a perfectly good cookie in the jar, and
 * made the five-day auto-logout indistinguishable from a working session
 * (US-00-001.15, US-00-001.3/.17).
 *
 * A renewed token arrives on the response header and is taken by ./client — see
 * ../session, which decides whether this platform may keep one.
 */
export function fetchSession(options: ReadOptions = {}) {
	return api.get("/me", userSchema, options)
}

/**
 * One authenticated call, made for the token that comes back with it.
 *
 * There is no refresh endpoint: the service mints a fresh JWT onto
 * `X-Session-Token` when the presented one is past half its life, so the
 * exchange IS an ordinary authenticated request (US-00-001.16). ../session
 * decides when to make it; this is what it makes.
 */
export function touchSession(options: ReadOptions = {}) {
	return fetchSession(options)
}

/**
 * Revokes a session server-side.
 *
 * ONE ROUTE, PUBLISHED: `POST /auth/sign-out` for a taxpayer and `POST
 * /expert/auth/sign-out` for the panel, both answering 204. `POST /auth/logout`
 * — the spelling this client used to send — is not a route the service has, so
 * Sign out cleared the browser and left the session live on the server
 * (US-00-001.18, US-01-005.23).
 *
 * Shared with ./panel-auth so the two audiences cannot drift into two
 * behaviours; the path is the only thing that differs.
 */
export async function revokeSession(route: string, options: ReadOptions = {}): Promise<void> {
	// `z.unknown()` because the answer is a 204 with no body — ./client returns
	// undefined without parsing, and there is nothing here worth a schema.
	await request(route, z.unknown(), { ...options, method: "POST" })
}

/**
 * Signs out.
 *
 * The server invalidates the session so the same token replayed returns 401
 * (US-00-001.18); clearing the local copy is ../session's job, and both have to
 * happen.
 */
export function signOut(options: ReadOptions = {}): Promise<void> {
	return revokeSession("/auth/sign-out", options)
}
